import { Suspense, useRef, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import TranslateIcon from '@mui/icons-material/Translate';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import BusinessIcon from '@mui/icons-material/Business';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import { AssistantLauncher } from '@/components/assistant/AssistantLauncher';
import { PoweredBy, ShellBrand } from '@/components/BrandMark';
import { ChangePasswordDialog } from '@/components/ChangePasswordDialog';
import { HelpLauncher } from '@/components/help/HelpLauncher';
import { Pill } from '@/components/Pill';
import { describeError, toast } from '@/components/Toast';
import { useActionTooltips } from '@/components/useActionTooltips';
import { ErrorBoundary, PageFallback } from '@/components/ErrorBoundary';
import { changeLanguage } from '@/i18n';
import { SAND } from '@/theme/theme';
import { NAV_SECTIONS } from './nav';

const DRAWER_WIDTH = 264;
const HEADER_HEIGHT = 60;

interface CompanyOption {
  id: string;
  name: string;
  nameAr: string | null;
  abbr: string;
}

/** "Walid Awny" → "WA"; one word gives one letter; nothing gives nothing. */
function initialsOf(fullName: string | null | undefined): string {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

export function AppShell() {
  const { t, i18n } = useTranslation();
  const { user, company, setCompany, logout, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [companyAnchor, setCompanyAnchor] = useState<null | HTMLElement>(null);
  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  // Every button explains itself on hover, from a shared dictionary.
  const mainRef = useRef<HTMLElement>(null);
  useActionTooltips(mainRef);

  const { data: companies = [] } = useQuery({
    queryKey: ['companies-mine'],
    queryFn: () => api.get<CompanyOption[]>('/companies/mine'),
  });

  const { data: unread } = useQuery({
    queryKey: ['notifications-unread'],
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
  });

  const activeCompany = companies.find((c) => c.id === company);
  const isArabic = i18n.language.startsWith('ar');
  const companyLabel = isArabic ? (activeCompany?.nameAr ?? activeCompany?.name) : activeCompany?.name;

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Spacer only: the brand sits in the header, which spans the full width. */}
      <Toolbar />
      {/* The sections scroll; the credit below stays pinned to the bottom. */}
      <Box sx={{ flex: 1, overflow: 'auto', pt: 1, pb: 3 }}>
        {/* Which app this is: the finance app carries its own label and tag here. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mx: 2.25,
            mt: 1.75,
            mb: 0.25,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.disabled',
          }}
        >
          <span>{t('Human resources')}</span>
          <Box
            component="span"
            sx={{
              fontSize: 10,
              px: '7px',
              py: '1px',
              borderRadius: 999,
              backgroundColor: SAND.tagBg,
              color: SAND.tagText,
              letterSpacing: 0,
              textTransform: 'none',
            }}
          >
            {t('HR app')}
          </Box>
        </Box>
        {NAV_SECTIONS.map((section) => {
          // Sections whose every entry is denied are hidden rather than shown disabled.
          const visible = section.items.filter(
            (item) =>
              (!item.doctype || !item.action || can(item.doctype, item.action)) &&
              (!item.role || user?.isAdmin === true || (user?.roles ?? []).includes(item.role)),
          );
          if (visible.length === 0) return null;

          return (
            <List
              key={section.key}
              dense
              disablePadding
              subheader={<ListSubheader disableSticky>{t(section.labelKey)}</ListSubheader>}
            >
              {visible.map((item) => (
                <ListItemButton
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  // A record's own page (/employees/:id) keeps its list highlighted.
                  selected={location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <item.icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t(item.labelKey)} primaryTypographyProps={{ fontSize: 14 }} />
                </ListItemButton>
              ))}
            </List>
          );
        })}
      </Box>
      <Divider />
      <Box sx={{ px: 2, py: 1.25 }}>
        <PoweredBy />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ gap: { xs: 1, sm: 1.5 }, px: { xs: 1.5, sm: 2.25 }, minHeight: HEADER_HEIGHT }}>
          <IconButton
            edge="start"
            aria-label={t('Open menu')}
            onClick={() => setMobileOpen((open) => !open)}
            sx={{ display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <ShellBrand />

          <Box sx={{ flex: 1 }} />

          {companies.length > 0 && (
            <>
              <Tooltip title={t('auth.company')}>
                <Pill
                  data-testid="company-switcher"
                  aria-haspopup="menu"
                  onClick={(event) => setCompanyAnchor(event.currentTarget)}
                  sx={{ width: { xs: 36, sm: 'auto' }, maxWidth: { sm: 260 }, px: { xs: 0, sm: '14px' }, justifyContent: 'center' }}
                >
                  <BusinessIcon />
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'block' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {companyLabel}
                  </Box>
                  <ExpandMoreIcon sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '18px !important', marginInlineEnd: -0.5 }} />
                </Pill>
              </Tooltip>
              <Menu
                anchorEl={companyAnchor}
                open={Boolean(companyAnchor)}
                onClose={() => setCompanyAnchor(null)}
              >
                {companies.map((option) => (
                  <MenuItem
                    key={option.id}
                    selected={option.id === company}
                    onClick={() => {
                      setCompanyAnchor(null);
                      if (option.id !== company) setCompany(option.id);
                    }}
                  >
                    {isArabic ? (option.nameAr ?? option.name) : option.name} ({option.abbr})
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          <Tooltip title={t('common.language')}>
            <Pill
              data-testid="language-toggle"
              aria-label={t('common.language')}
              onClick={() => void changeLanguage(isArabic ? 'en' : 'ar')}
              sx={{ px: { xs: 0, sm: '14px' }, width: { xs: 36, sm: 'auto' }, justifyContent: 'center' }}
            >
              <TranslateIcon />
              {/* The other language, named in itself, as on a bilingual sign. */}
              <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }} lang={isArabic ? 'en' : 'ar'}>
                {isArabic ? 'English' : 'عربي'}
              </Box>
            </Pill>
          </Tooltip>

          <Tooltip title={t('Notifications')}>
            <Pill
              iconOnly
              data-testid="notifications"
              aria-label={t('Notifications')}
              onClick={() => navigate('/notifications')}
            >
              <Badge badgeContent={unread?.count ?? 0} color="error" overlap="circular">
                <NotificationsNoneIcon />
              </Badge>
            </Pill>
          </Tooltip>

          {/* How to do things — the guide, searched. Not the data assistant. */}
          <HelpLauncher />

          <Tooltip title={user?.fullName ?? t('Account')}>
            <IconButton
              data-testid="account-menu"
              aria-label={t('Account')}
              aria-haspopup="menu"
              onClick={(event) => setUserAnchor(event.currentTarget)}
              sx={{ p: 0 }}
            >
              <Avatar sx={{ width: 34, height: 34 }}>{initialsOf(user?.fullName) || undefined}</Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={userAnchor}
            open={Boolean(userAnchor)}
            onClose={() => setUserAnchor(null)}
          >
            <MenuItem disabled>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {user?.fullName}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setUserAnchor(null);
                setChangingPassword(true);
              }}
            >
              {t('auth.change_password')}
            </MenuItem>
            {/*
              Ends every other session, not this one — the control for a laptop
              left signed in somewhere else.
            */}
            <MenuItem
              onClick={async () => {
                setUserAnchor(null);
                try {
                  await api.post('/auth/logout-all');
                } catch (err) {
                  // Said rather than swallowed: the other sessions are still
                  // open, and this one stays signed in so it can be retried.
                  toast.error(describeError(err));
                  return;
                }
                await logout();
                navigate('/login');
              }}
            >
              {t('auth.sign_out_everywhere')}
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={async () => {
                setUserAnchor(null);
                await logout();
                navigate('/login');
              }}
            >
              {t('auth.sign_out')}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <ChangePasswordDialog
        open={changingPassword}
        onClose={() => setChangingPassword(false)}
      />

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/*
        minWidth 0: a flex child otherwise grows to its widest content, so one
        wide table pushed the whole page sideways on a phone instead of
        scrolling inside its own card.
      */}
      <Box
        component="main"
        ref={mainRef}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 3.5 },
          pt: { xs: 2, md: 3 },
          pb: { xs: 4, md: 7.5 },
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        <Toolbar />
        <ErrorBoundary key={location.pathname}>
          <Suspense fallback={<PageFallback />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </Box>

      <AssistantLauncher />
    </Box>
  );
}
